import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { ReactSVG } from "react-svg";
import { webURL } from "../../../services/API";
import { workflowItems } from "../../workflow";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { BarChart3, Zap, X, Activity, TrendingUp, XCircle, DollarSign, MessageSquare, Info, ChevronDown, ChevronRight, Users, Clock } from "lucide-react";
import styled from "styled-components";

type BillingSummaryItem = {
   blockName: string;
   blockAction: string;
   credentialSource: 'dragify' | 'user';
   executionCount: number;
   inputTokens: number;
   outputTokens: number;
   cachedInputTokens?: number;
   embeddingTokens: number;
   llmCallCount?: number;
   toolCallCount?: number;
   baseCredits: number;
   tokenCredits: number;
   marginCredits: number;
   roundingCredits?: number;
   totalCredits: number;
   providerCostEgp?: number;
   messageCostEgp?: number;
   totalCostEgp?: number;
   model?: string;
   estimatedCount?: number;
   realCount?: number;
};

type BillingTotals = {
   executionCount: number;
   inputTokens: number;
   outputTokens: number;
   cachedInputTokens: number;
   totalCredits: number;
   totalCostEgp: number;
   messageCostEgp: number;
   estimatedCount: number;
   realCount: number;
};

type ConversationItem = {
   conversationId: string;
   customerId?: string;
   customerName?: string;
   messageCount: number;
   totalInputTokens: number;
   totalOutputTokens: number;
   totalCachedTokens: number;
   totalLlmCalls: number;
   totalToolCalls: number;
   totalCredits: number;
   totalCostEgp: number;
   messageCostEgp: number;
   models: string[];
   lastMessageAt: string;
   firstMessageAt: string;
};

type CustomerSummaryItem = {
   customerId: string;
   customerName?: string;
   conversationCount: number;
   messageCount: number;
   totalCredits: number;
   totalCostEgp: number;
   lastActivityAt: string;
};

type MessageRow = {
   _id: string;
   blockName: string;
   model?: string;
   inputTokens: number;
   outputTokens: number;
   cachedInputTokens?: number;
   llmCallCount?: number;
   toolCallCount?: number;
   totalCredits: number;
   totalCostEgp: number;
   messageCostEgp?: number;
   isEstimatedTokens?: boolean;
   createdAt: string;
};

type KnowledgeLogRow = {
   _id: string;
   type: 'success' | 'failure';
   source: string;
   summary?: string;
   description?: string;
   registration: string;
};

export function ProjectLog({ projectId, nodes }: { projectId: string, nodes: any[] }) {
   const settingsIsRun = useSelector((state: RootState) => state.settings.data.isRunForProcess);
   const isWorkflowRun = useSelector((state: RootState) => state.settings.data.isWorkflowRun);
   const [isToggle, setIsToggle] = React.useState(false);
   const [activeTab, setActiveTab] = React.useState<'billing' | 'conversations' | 'legacy' | 'knowledge'>('billing');
   const [items, setItems] = React.useState<{ name: string, credits: number, count: number, isActive: boolean }[]>([]);
   const [billingSummary, setBillingSummary] = React.useState<BillingSummaryItem[]>([]);
   const [billingTotals, setBillingTotals] = React.useState<BillingTotals | null>(null);
   const [billingConfig, setBillingConfig] = React.useState<{ marginPercent: number, creditValueEgp: number } | null>(null);
   const [conversations, setConversations] = React.useState<ConversationItem[]>([]);
   const [customerSummary, setCustomerSummary] = React.useState<CustomerSummaryItem[]>([]);
   const [knowledgeRows, setKnowledgeRows] = React.useState<KnowledgeLogRow[]>([]);
   const [knowledgeTotalCredits, setKnowledgeTotalCredits] = React.useState(0);
   const [expandedRow, setExpandedRow] = React.useState<string | null>(null);
   const [expandedModelRow, setExpandedModelRow] = React.useState<string | null>(null);
   const [expandedConv, setExpandedConv] = React.useState<string | null>(null);
   const [convMessages, setConvMessages] = React.useState<{ [convId: string]: MessageRow[] }>({});
   const [convMessagesLoading, setConvMessagesLoading] = React.useState<{ [convId: string]: boolean }>({});

   const totalRequests = React.useMemo(() => items.reduce((total, item) => total + item.count, 0), [items]);
   const totalCredits = React.useMemo(() => items.reduce((total, item) => total + item.credits, 0), [items]);
   const displayCredits = billingSummary.length ? (billingTotals?.totalCredits ?? totalCredits) : totalCredits;
   const displayCostEgp = billingTotals?.totalCostEgp ?? 0;
   const conversationCredits = React.useMemo(() => conversations.reduce((total, item) => total + Number(item.totalCredits || 0), 0), [conversations]);
   const conversationCostEgp = React.useMemo(() => conversations.reduce((total, item) => total + Number(item.totalCostEgp || 0), 0), [conversations]);
   const conversationExecutions = React.useMemo(() => conversations.reduce((total, item) => total + Number(item.messageCount || 0), 0), [conversations]);
   const knowledgeEventCount = knowledgeRows.length;
   const cardCredits = activeTab === 'legacy' ? totalCredits : activeTab === 'conversations' ? conversationCredits : activeTab === 'knowledge' ? knowledgeTotalCredits : displayCredits;
   const cardCostEgp = activeTab === 'legacy' ? 0 : activeTab === 'conversations' ? conversationCostEgp : activeTab === 'knowledge' ? 0 : displayCostEgp;
   const cardExecutions = activeTab === 'legacy' ? totalRequests : activeTab === 'conversations' ? conversationExecutions : activeTab === 'knowledge' ? knowledgeEventCount : (billingTotals?.executionCount ?? totalRequests);
   const activeIndexes = React.useMemo(() => new Set(nodes.map((node: any) => node.data.index)), [nodes]);

   const fallbackNodeRows = React.useMemo(() => {
      return (nodes || [])
         .filter((node: any) => Boolean(node?.data?.index))
         .map((node: any) => ({
            name: String(node.data.index),
            credits: 0,
            count: 0,
            isActive: true,
         }));
   }, [nodes]);

   React.useEffect(() => {
      if (!projectId) {
         setItems(fallbackNodeRows);
         setBillingSummary([]);
         setBillingConfig(null);
         setBillingTotals(null);
         setConversations([]);
         setCustomerSummary([]);
         setKnowledgeRows([]);
         setKnowledgeTotalCredits(0);
         return;
      }

      (async function () {
         const [legacyResult, billingResult, convResult, knowledgeResult] = await Promise.allSettled([
            axios.get(`${webURL}/process?projectId=${projectId}`),
            axios.get(`${webURL}/process/billing-usage?projectId=${projectId}&limit=150`),
            axios.get(`${webURL}/process/billing-usage/conversations?projectId=${projectId}&limit=100`),
            axios.get(`${webURL}/logs?source=knowledge&page=1&pageSize=50`),
         ]);

         const legacyData = legacyResult.status === 'fulfilled' && Array.isArray(legacyResult.value?.data)
            ? legacyResult.value.data : [];

         const processRows = legacyData.map((item: any) => ({
            ...item,
            isActive: nodes.some((node: any) => node.data.index === item.name)
         }));

         if (processRows.length) {
            const byName = new Map(processRows.map((row: any) => [row.name, row]));
            const merged = [...processRows];
            for (const fallback of fallbackNodeRows) {
               if (!byName.has(fallback.name)) merged.push(fallback);
            }
            setItems(merged);
         } else {
            setItems(fallbackNodeRows);
         }

         if (billingResult.status === 'fulfilled') {
            const billingData = billingResult.value?.data || {};
            setBillingSummary(Array.isArray(billingData.summary) ? billingData.summary : []);
            setBillingConfig(billingData.config || null);
            setBillingTotals(billingData.totals || null);
         } else {
            setBillingSummary([]);
            setBillingConfig(null);
            setBillingTotals(null);
         }

         if (convResult.status === 'fulfilled') {
            const convData = convResult.value?.data || {};
            setConversations(Array.isArray(convData.conversations) ? convData.conversations : []);
            setCustomerSummary(Array.isArray(convData.customerSummary) ? convData.customerSummary : []);
         } else {
            setConversations([]);
            setCustomerSummary([]);
         }

         if (knowledgeResult.status === 'fulfilled') {
            const knowledgeData = knowledgeResult.value?.data || {};
            setKnowledgeRows(Array.isArray(knowledgeData.rows) ? knowledgeData.rows : []);
            setKnowledgeTotalCredits(Number(knowledgeData.totalCredits) || 0);
         } else {
            setKnowledgeRows([]);
            setKnowledgeTotalCredits(0);
         }
      })();
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [projectId, settingsIsRun, isWorkflowRun, isToggle]);

   async function loadConvMessages(convId: string) {
      if (convMessages[convId] || convMessagesLoading[convId]) return;
      setConvMessagesLoading(prev => ({ ...prev, [convId]: true }));
      try {
         const res = await axios.get(`${webURL}/process/billing-usage?projectId=${projectId}&conversationId=${encodeURIComponent(convId)}&limit=100`);
         const rows: MessageRow[] = Array.isArray(res.data?.rows) ? res.data.rows : [];
         setConvMessages(prev => ({ ...prev, [convId]: rows }));
      } catch {
         setConvMessages(prev => ({ ...prev, [convId]: [] }));
      } finally {
         setConvMessagesLoading(prev => ({ ...prev, [convId]: false }));
      }
   }

   function toggleConv(convId: string) {
      if (expandedConv === convId) {
         setExpandedConv(null);
      } else {
         setExpandedConv(convId);
         loadConvMessages(convId);
      }
   }

   const hasBilling = billingSummary.length > 0;

   type BlockGroup = {
      blockName: string;
      blockAction: string;
      credentialSource: 'dragify' | 'user';
      isActive: boolean;
      modelRows: BillingSummaryItem[];
      totalExecutionCount: number;
      totalInputTokens: number;
      totalOutputTokens: number;
      totalCredits: number;
      totalCostEgp: number;
      estimatedCount: number;
      realCount: number;
   };

   const billingByBlock = React.useMemo<BlockGroup[]>(() => {
      const map = new Map<string, BlockGroup>();
      for (const row of billingSummary) {
         const key = `${row.blockName}||${row.credentialSource}`;
         if (!map.has(key)) {
            map.set(key, {
               blockName: row.blockName,
               blockAction: row.blockAction,
               credentialSource: row.credentialSource,
               isActive: activeIndexes.has(row.blockName),
               modelRows: [],
               totalExecutionCount: 0,
               totalInputTokens: 0,
               totalOutputTokens: 0,
               totalCredits: 0,
               totalCostEgp: 0,
               estimatedCount: 0,
               realCount: 0,
            });
         }
         const g = map.get(key)!;
         g.modelRows.push(row);
         g.totalExecutionCount += Number(row.executionCount || 0);
         g.totalInputTokens    += Number(row.inputTokens    || 0);
         g.totalOutputTokens   += Number(row.outputTokens   || 0);
         g.totalCredits        += Number(row.totalCredits   || 0);
         g.totalCostEgp        += Number(row.totalCostEgp   || 0);
         g.estimatedCount      += Number(row.estimatedCount || 0);
         g.realCount           += Number(row.realCount      || 0);
      }
      return [...map.values()];
   }, [billingSummary, activeIndexes]);

   return (
      <div>
         <div className="boxLog processReport" onClick={ _=> setIsToggle(true) }>
            <div className="icon"><i className="ti ti-file-text" aria-hidden="true" /></div>
            <div className="info">
               <span>Process Report</span>
               <div>
                  <span><BarChart3 size="12px" color="#E8963C"/>{ totalRequests } Total</span>
                  <span><Zap size="12px" color="#8172FF" />{ Number(totalCredits).toFixed(2) } Credits</span>
               </div>
            </div>
         </div>
         { isToggle ? <Popup onClick={ _=> setIsToggle(false) }>
            <div className="direct" onClick={ e => e.stopPropagation() }>
               <div className="close" onClick={ _=> setIsToggle(false) }><X size={ 18 } /></div>
               <div className="iconTitle">
                  <div><Activity size={ 18 } /></div>
                  <h3>Process Report <span>For current blocks</span></h3>
               </div>

               {/* Summary Cards */}
               <div className="cards">
                  <div className="card blue">
                     <div><span className="title">Total Credits</span><span className="num">{ Number(cardCredits).toFixed(2) }</span></div>
                     <Zap size={ 26 } />
                  </div>
                  <div className="card green">
                     <div><span className="title">Cost (EGP)</span><span className="num">{ cardCostEgp.toFixed(4) }</span></div>
                     <DollarSign size={ 26 } />
                  </div>
                  <div className="card purple">
                     <div><span className="title">Executions</span><span className="num">{ cardExecutions }</span></div>
                     <TrendingUp size={ 26 } />
                  </div>
                  <div className="card teal" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('conversations')}>
                     <div><span className="title">Conversations</span><span className="num">{ conversations.length }</span></div>
                     <MessageSquare size={ 26 } />
                  </div>
               </div>

               {/* Tab Bar */}
               <div className="tabBar">
                  <button className={activeTab === 'billing' ? 'active' : ''} onClick={() => setActiveTab('billing')}>
                     <Zap size={12} /> Blocks Billing
                  </button>
                  <button className={activeTab === 'conversations' ? 'active' : ''} onClick={() => setActiveTab('conversations')}>
                     <MessageSquare size={12} /> Conversations
                     { conversations.length > 0 && <span className="tabCount">{conversations.length}</span> }
                  </button>
                  <button className={activeTab === 'knowledge' ? 'active' : ''} onClick={() => setActiveTab('knowledge')}>
                     <Activity size={12} /> Knowledge
                     { knowledgeRows.length > 0 && <span className="tabCount">{knowledgeRows.length}</span> }
                  </button>
                  <button className={activeTab === 'legacy' ? 'active' : ''} onClick={() => setActiveTab('legacy')}>
                     <BarChart3 size={12} /> Legacy Counts
                  </button>
               </div>

               {/* Tab: Blocks Billing */}
               { activeTab === 'billing' && <>
                  { hasBilling && billingConfig && (
                     <div className="billingHint">
                        <Info size={11} />
                        Margin {billingConfig.marginPercent}% · Credit {billingConfig.creditValueEgp} EGP
                        { (billingTotals?.estimatedCount ?? 0) > 0 && (
                           <span className="estimatedBadge">~ {billingTotals!.estimatedCount} estimated</span>
                        )}
                     </div>
                  )}
                  <div className="table">
                     { hasBilling ? <>
                        <div className="billingGrid billingHead">
                           <span>Block</span><span>Model</span><span>Runs</span>
                           <span>Tokens In / Out</span><span>Credits</span><span>Cost EGP</span>
                        </div>
                        <div className="scroll">
                           { billingByBlock.map((group) => {
                              const icon: any = workflowItems.find(i => group.blockName.includes(i.action) || group.blockAction === i.action)?.icon;
                              const blockKey = `${group.blockName}||${group.credentialSource}`;
                              const isExpanded = expandedRow === blockKey;
                              const hasEstimated = group.estimatedCount > 0 && group.realCount === 0;
                              const multiModel = group.modelRows.length > 1;
                              return (
                                 <React.Fragment key={blockKey}>
                                    {/* Parent block row */}
                                    <div className={`billingGrid billingRow${isExpanded ? ' expanded' : ''}`} onClick={() => setExpandedRow(isExpanded ? null : blockKey)}>
                                       <span className="blockCell">
                                          { multiModel
                                             ? <span className="chevron">{ isExpanded ? <ChevronDown size={13}/> : <ChevronRight size={13}/> }</span>
                                             : <span className="icon">{ icon ? (typeof icon === 'string' ? <ReactSVG className="otherSvg" src={ icon } /> : <FontAwesomeIcon icon={ icon } />) : null }</span>
                                          }
                                          <div>
                                             <span className="name">{ group.blockName }</span>
                                             <div className="badges">
                                                { group.isActive ? <div className="status"><Activity size={10} /><span>active</span></div> : <div className="status remove"><XCircle size={10} /><span>deleted</span></div> }
                                                <span className={group.credentialSource === 'user' ? 'keyBadge userKey' : 'keyBadge dragifyKey'}>{ group.credentialSource }</span>
                                                { hasEstimated && <span className="estimBadge">~est</span> }
                                             </div>
                                          </div>
                                       </span>
                                       <span className="modelCell" title={group.modelRows.map(r => r.model).filter(Boolean).join(', ')}>
                                          { multiModel ? `${group.modelRows.length} models` : (group.modelRows[0]?.model || '—') }
                                       </span>
                                       <span>{ group.totalExecutionCount }</span>
                                       <span>{ group.totalInputTokens.toLocaleString() } / { group.totalOutputTokens.toLocaleString() }</span>
                                       <span className="creditsCell">{ group.totalCredits.toFixed(2) }</span>
                                       <span className="costCell">{ group.totalCostEgp.toFixed(4) }</span>
                                    </div>

                                    {/* Model sub-rows — visible when parent is expanded */}
                                    { multiModel && isExpanded && group.modelRows.map((mrow) => {
                                       const mKey = `${blockKey}||${mrow.model}`;
                                       const mExpanded = expandedModelRow === mKey;
                                       return (
                                          <React.Fragment key={mKey}>
                                             <div className="billingGrid billingRow modelSubRow" style={{ paddingLeft: 48, background: 'rgb(250,251,253)' }}
                                                onClick={(e) => { e.stopPropagation(); setExpandedModelRow(mExpanded ? null : mKey); }}>
                                                <span className="blockCell" style={{ paddingLeft: 8 }}>
                                                   <div style={{ width: 8, height: 1, background: 'rgb(203,213,225)', marginRight: 8, flexShrink: 0 }} />
                                                   <div><span className="name" style={{ fontSize: 12, color: 'rgb(71,85,105)' }}>{ mrow.model || '—' }</span></div>
                                                </span>
                                                <span className="modelCell">{ mrow.model || '—' }</span>
                                                <span style={{ fontSize: 12 }}>{ mrow.executionCount }</span>
                                                <span style={{ fontSize: 12 }}>{ (mrow.inputTokens || 0).toLocaleString() } / { (mrow.outputTokens || 0).toLocaleString() }</span>
                                                <span className="creditsCell" style={{ fontSize: 12 }}>{ Number(mrow.totalCredits || 0).toFixed(2) }</span>
                                                <span className="costCell" style={{ fontSize: 12 }}>{ Number(mrow.totalCostEgp || 0).toFixed(4) }</span>
                                             </div>
                                             { mExpanded && (
                                                <div className="expandedRow" style={{ paddingLeft: 80 }}>
                                                   <div className="expandGrid">
                                                      <div><span>Input Tokens</span><strong>{ (mrow.inputTokens || 0).toLocaleString() }</strong></div>
                                                      <div><span>Output Tokens</span><strong>{ (mrow.outputTokens || 0).toLocaleString() }</strong></div>
                                                      <div><span>LLM Calls</span><strong>{ mrow.llmCallCount ?? '—' }</strong></div>
                                                      <div><span>Tool Calls</span><strong>{ mrow.toolCallCount ?? '—' }</strong></div>
                                                      <div><span>Provider Cost</span><strong>{ Number(mrow.providerCostEgp || 0).toFixed(4) } EGP</strong></div>
                                                   </div>
                                                </div>
                                             )}
                                          </React.Fragment>
                                       );
                                    })}

                                    {/* Single-model expanded detail */}
                                    { !multiModel && isExpanded && (() => { const mrow = group.modelRows[0]; return (
                                       <div className="expandedRow">
                                          <div className="expandGrid">
                                             <div><span>Input Tokens</span><strong>{ (mrow?.inputTokens || 0).toLocaleString() }</strong></div>
                                             <div><span>Output Tokens</span><strong>{ (mrow?.outputTokens || 0).toLocaleString() }</strong></div>
                                             <div><span>LLM Calls</span><strong>{ mrow?.llmCallCount ?? '—' }</strong></div>
                                             <div><span>Tool Calls</span><strong>{ mrow?.toolCallCount ?? '—' }</strong></div>
                                             <div><span>Provider Cost</span><strong>{ Number(mrow?.providerCostEgp || 0).toFixed(4) } EGP</strong></div>
                                          </div>
                                       </div>
                                    ); })()}
                                 </React.Fragment>
                              );
                           })}
                        </div>
                     </> : <div className="empty">No billing data yet. Run the workflow to see detailed AI billing.</div> }
                  </div>
               </>}

               {/* Tab: Conversations */}
               { activeTab === 'conversations' && <div className="table">
                  { conversations.length > 0 ? <>
                     {/* Per-Customer summary */}
                     { customerSummary.length > 0 && (
                        <div className="customerSummaryBar">
                           <span><Users size={11} /> Customers: {customerSummary.length}</span>
                           { customerSummary.map(c => (
                              <span key={c.customerId} className="customerChip" title={`${c.messageCount} messages · ${c.totalCostEgp.toFixed(4)} EGP`}>
                                 {c.customerName || c.customerId} · {Number(c.totalCostEgp || 0).toFixed(3)} EGP
                              </span>
                           ))}
                        </div>
                     )}
                     {/* Conversation list header */}
                     <div className="billingGrid convHead">
                        <span>Conversation</span>
                        <span>Customer</span>
                        <span>Messages</span>
                        <span>Tokens In / Out</span>
                        <span>Avg/msg EGP</span>
                        <span>Total EGP</span>
                     </div>
                     <div className="scroll">
                        { conversations.map(conv => {
                           const isOpen = expandedConv === conv.conversationId;
                           const msgs = convMessages[conv.conversationId] || [];
                           const isLoading = convMessagesLoading[conv.conversationId];
                           const avgPerMsg = conv.messageCount > 0 ? (conv.totalCostEgp / conv.messageCount) : 0;
                           return (
                              <React.Fragment key={conv.conversationId}>
                                 <div className={`billingGrid billingRow convRow${isOpen ? ' expanded' : ''}`} onClick={() => toggleConv(conv.conversationId)}>
                                    <span className="blockCell">
                                       <span className="chevron">{ isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} /> }</span>
                                       <div>
                                          <span className="name" title={conv.conversationId}>{ conv.conversationId.length > 22 ? `…${conv.conversationId.slice(-18)}` : conv.conversationId }</span>
                                          <div className="badges">
                                             <span className="status"><Clock size={10} /><span>{ conv.lastMessageAt ? new Date(conv.lastMessageAt).toLocaleDateString() : '—' }</span></span>
                                          </div>
                                       </div>
                                    </span>
                                    <span className="modelCell">{ conv.customerName || conv.customerId || '—' }</span>
                                    <span>{ conv.messageCount }</span>
                                    <span>{ (conv.totalInputTokens || 0).toLocaleString() } / { (conv.totalOutputTokens || 0).toLocaleString() }</span>
                                    <span className="costCell">{ avgPerMsg.toFixed(4) }</span>
                                    <span className="costCell">{ Number(conv.totalCostEgp || 0).toFixed(4) }</span>
                                 </div>
                                 { isOpen && (
                                    <div className="expandedRow convMessagesPanel">
                                       { isLoading ? (
                                          <div className="convLoading">Loading messages…</div>
                                       ) : msgs.length === 0 ? (
                                          <div className="convLoading">No message records for this conversation.</div>
                                       ) : (<>
                                          <div className="billingGrid msgHead">
                                             <span>Block</span>
                                             <span>Model</span>
                                             <span>Time</span>
                                             <span>Tokens In / Out</span>
                                             <span>Credits</span>
                                             <span>Cost EGP</span>
                                          </div>
                                          { msgs.map((msg, idx) => (
                                             <div key={msg._id || idx} className="billingGrid msgRow">
                                                <span className="name">{ msg.blockName }</span>
                                                <span className="modelCell">{ msg.model || '—' }</span>
                                                <span className="modelCell">{ msg.createdAt ? new Date(msg.createdAt).toLocaleTimeString() : '—' }</span>
                                                <span>{ (msg.inputTokens || 0).toLocaleString() } / { (msg.outputTokens || 0).toLocaleString() }</span>
                                                <span className="creditsCell">{ Number(msg.totalCredits || 0).toFixed(3) }</span>
                                                <span className="costCell">
                                                   { Number(msg.messageCostEgp ?? msg.totalCostEgp ?? 0).toFixed(4) }
                                                   { msg.isEstimatedTokens && <span className="estimBadge" style={{ marginLeft: 4 }}>~est</span> }
                                                </span>
                                             </div>
                                          ))}
                                          <div className="convTotalRow">
                                             <span>Total for this conversation</span>
                                             <span className="creditsCell">{ Number(conv.totalCredits || 0).toFixed(2) } credits</span>
                                             <span className="costCell">{ Number(conv.totalCostEgp || 0).toFixed(4) } EGP</span>
                                          </div>
                                       </>)}
                                    </div>
                                 )}
                              </React.Fragment>
                           );
                        })}
                     </div>
                  </> : <div className="empty">No conversations found. Conversations appear when the AI Agent block is used with chat memory.</div> }
               </div> }

               {/* Tab: Legacy Counts */}
               { activeTab === 'legacy' && <div className="table">
                  <div className="billingGrid billingHead legacyHead">
                     <span>Block Name</span><span>Status</span><span>Credits</span><span>Processes</span>
                  </div>
                  <div className="scroll">
                     { items.length ? items.map(item => {
                        const icon: any = workflowItems.find(i => item.name.includes(i.action))?.icon;
                        return (
                           <div key={ item.name } className="billingGrid billingRow legacyRow">
                              <span className="blockCell">
                                 <span className="icon">{ icon ? (typeof icon === 'string' ? <ReactSVG className="otherSvg" src={ icon } /> : <FontAwesomeIcon icon={ icon } />) : null }</span>
                                 <span className="name">{ item.name }</span>
                              </span>
                              <span>{ item.isActive ? <div className="status"><Activity size={10} /><span>active</span></div> : <div className="status remove"><XCircle size={10} /><span>deleted</span></div> }</span>
                              <span>{ item.credits }</span>
                              <span>{ item.count }</span>
                           </div>
                        );
                     }) : <div className="empty">There are no blocks in the workflow!</div> }
                  </div>
               </div> }

               {/* Tab: Knowledge */}
               { activeTab === 'knowledge' && <div className="table">
                  <div className="billingGrid billingHead knowledgeHead">
                     <span>Time</span><span>Type</span><span>Event</span><span>Details</span>
                  </div>
                  <div className="scroll">
                     { knowledgeRows.length ? knowledgeRows.map((row) => (
                        <div key={ row._id } className="billingGrid billingRow knowledgeRow">
                           <span className="modelCell">{ row.registration ? new Date(row.registration).toLocaleString() : '—' }</span>
                           <span>{ row.type === 'success' ? <div className="status"><Activity size={10} /><span>success</span></div> : <div className="status remove"><XCircle size={10} /><span>failure</span></div> }</span>
                           <span className="blockCell"><span className="name">{ row.summary || 'Knowledge event' }</span></span>
                           <span className="modelCell" title={ row.description }>{ row.description || '—' }</span>
                        </div>
                     )) : <div className="empty">No knowledge-base events yet. Index or query a folder to see logs.</div> }
                  </div>
               </div> }

            </div>
         </Popup> : null }
      </div>
   );
}

const Popup = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   position: fixed;
   top: 0;
   left: 0;
   width: 100%;
   height: 100%;
   background-color: rgba(15, 23, 42, 0.6);
   z-index: 999;
   backdrop-filter: blur(2px);

   .direct {
      display: flex;
      flex-direction: column;
      position: relative;
      background-color: #fff;
      padding: 28px 28px 20px;
      border-radius: 12px;
      box-shadow: 0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08);
      width: 920px;
      max-height: 90vh;
      overflow: hidden;
      box-sizing: border-box;

      .close {
         position: absolute;
         top: 18px;
         right: 18px;
         opacity: 0.5;
         padding: 6px;
         border-radius: 6px;
         transition: all .15s ease;
         cursor: pointer;
         display: flex;
         align-items: center;

         &:hover {
            opacity: 1;
            background-color: rgb(241, 245, 249);
         }
      }

      .iconTitle {
         display: flex;
         align-items: center;
         margin-bottom: 20px;

         > div {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 34px;
            height: 34px;
            background-image: linear-gradient(135deg, #3b82f6, #06b6d4);
            color: #fff;
            border-radius: 8px;
            margin-right: 10px;
         }
         h3 {
            display: flex;
            align-items: center;
            font-weight: 600;
            font-size: 16px;
            color: rgb(15, 23, 42);
            margin: 0;

            span {
               font-weight: 500;
               font-size: 12px;
               margin-left: 8px;
               background-color: rgb(219, 234, 254);
               color: rgb(30, 64, 175);
               border: 1px solid rgb(191, 219, 254);
               border-radius: 20px;
               padding: 2px 10px;
            }
         }
      }

      .icon {
         display: flex;
         align-items: center;
         justify-content: center;
         width: 36px !important;
         height: 36px;
         background-color: rgb(248, 250, 252);
         border: 1px solid rgb(226, 232, 240);
         margin-right: 10px;
         border-radius: 8px;
         flex-shrink: 0;

         .otherSvg {
            width: 16px !important;
            height: 16px !important;
            svg { width: 100% !important; height: 100% !important; }
         }
      }

      .cards {
         display: grid;
         grid-template-columns: repeat(4, 1fr);
         gap: 10px;
         margin-bottom: 14px;

         .card {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 12px 14px;
            border-radius: 10px;
            border: 1px solid transparent;

            > div {
               display: flex;
               flex-direction: column;
               span.title {
                  font-size: 11px;
                  font-weight: 500;
                  text-transform: uppercase;
                  letter-spacing: 0.04em;
                  opacity: 0.7;
                  margin-bottom: 3px;
               }
               span.num {
                  font-size: 20px;
                  font-weight: 700;
                  line-height: 1;
               }
            }
            svg { opacity: 0.35; }

            &.blue {
               background: linear-gradient(135deg, #eff6ff, #ecfeff);
               border-color: rgb(191, 219, 254);
               color: rgb(30, 64, 175);
            }
            &.green {
               background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
               border-color: rgb(187, 247, 208);
               color: rgb(22, 101, 52);
            }
            &.purple {
               background: linear-gradient(135deg, #faf5ff, #f5f3ff);
               border-color: rgb(216, 180, 254);
               color: rgb(88, 28, 135);
            }
            &.teal {
               background: linear-gradient(135deg, #f0fdfa, #ecfeff);
               border-color: rgb(153, 246, 228);
               color: rgb(17, 94, 89);
            }
         }
      }

      .billingHint {
         display: flex;
         align-items: center;
         gap: 5px;
         font-size: 11.5px;
         color: rgb(71, 85, 105);
         padding: 6px 10px;
         background-color: rgb(248, 250, 252);
         border: 1px solid rgb(226, 232, 240);
         border-radius: 7px;
         margin-bottom: 10px;

         .estimatedBadge {
            margin-left: 6px;
            background-color: rgb(254, 243, 199);
            border: 1px solid rgb(253, 230, 138);
            color: rgb(120, 53, 15);
            border-radius: 20px;
            padding: 1px 8px;
            font-size: 11px;
            font-weight: 600;
         }
      }

      .table {
         display: flex;
         flex-direction: column;
         flex: 1;
         border: 1px solid rgb(226, 232, 240);
         border-radius: 10px;
         overflow: hidden;
         min-height: 0;

         .billingGrid {
            display: grid;
            align-items: center;
            column-gap: 10px;
            padding: 0 14px;
            box-sizing: border-box;

            &:not(.legacyHead):not(.legacyRow) {
               grid-template-columns: 2.6fr 1.4fr 0.5fr 1.4fr 0.7fr 0.7fr;
            }
            &.legacyHead, &.legacyRow {
               grid-template-columns: 2.5fr 1fr 0.7fr 0.7fr;
            }

            > span {
               font-size: 12.5px;
               min-width: 0;
               overflow: hidden;
               text-overflow: ellipsis;
               white-space: nowrap;
            }

            .blockCell {
               display: flex;
               align-items: center;
               min-width: 0;

               > div {
                  display: flex;
                  flex-direction: column;
                  min-width: 0;
               }
               .name {
                  font-weight: 500;
                  font-size: 12.5px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                  color: rgb(15, 23, 42);
               }
               .badges {
                  display: flex;
                  align-items: center;
                  gap: 4px;
                  flex-wrap: wrap;
                  margin-top: 3px;
               }
            }

            .modelCell {
               font-size: 11px;
               color: rgb(100, 116, 139);
               overflow: hidden;
               text-overflow: ellipsis;
               white-space: nowrap;
            }

            .creditsCell { font-weight: 600; color: rgb(30, 64, 175); }
            .costCell    { font-weight: 600; color: rgb(22, 101, 52); }

            &.billingHead {
               font-weight: 600;
               font-size: 11px;
               text-transform: uppercase;
               letter-spacing: 0.04em;
               color: rgb(71, 85, 105);
               background: linear-gradient(to right, #f8fafc, #f1f5f9);
               border-bottom: 1px solid rgb(226, 232, 240);
               min-height: 38px;
            }

            &.billingRow {
               min-height: 54px;
               cursor: pointer;
               transition: background 0.12s;
               border-bottom: 1px solid rgb(241, 245, 249);

               &:hover { background-color: rgb(248, 250, 252); }
               &.expanded { background-color: rgb(239, 246, 255); }
               &:last-of-type { border-bottom: none; }
            }
         }

         .expandedRow {
            background-color: rgb(248, 250, 252);
            border-bottom: 1px solid rgb(226, 232, 240);
            padding: 10px 14px 12px 60px;

            .expandGrid {
               display: grid;
               grid-template-columns: repeat(4, 1fr);
               gap: 8px 16px;

               > div {
                  display: flex;
                  flex-direction: column;
                  gap: 2px;

                  span {
                     font-size: 10.5px;
                     color: rgb(100, 116, 139);
                     text-transform: uppercase;
                     letter-spacing: 0.04em;
                  }
                  strong {
                     font-size: 13px;
                     font-weight: 600;
                     color: rgb(30, 41, 59);
                  }
               }
            }
         }

         .scroll {
            overflow-y: auto;
            flex: 1;
            min-height: 0;

            &::-webkit-scrollbar { width: 4px; }
            &::-webkit-scrollbar-track { background: transparent; }
            &::-webkit-scrollbar-thumb { background: rgb(203, 213, 225); border-radius: 4px; }
         }

         .billingGrid {
            &.convHead, &.msgHead {
               grid-template-columns: 2.6fr 1.4fr 0.5fr 1.4fr 0.7fr 0.7fr;
               font-weight: 600;
               font-size: 11px;
               text-transform: uppercase;
               letter-spacing: 0.04em;
               color: rgb(71, 85, 105);
               background: linear-gradient(to right, #f8fafc, #f1f5f9);
               border-bottom: 1px solid rgb(226, 232, 240);
               min-height: 36px;
            }
            &.msgHead {
               background: rgb(241, 245, 249);
               min-height: 30px;
               padding: 0 10px;
            }
            &.msgRow {
               grid-template-columns: 2.6fr 1.4fr 0.5fr 1.4fr 0.7fr 0.7fr;
               min-height: 40px;
               border-bottom: 1px solid rgb(248, 250, 252);
               padding: 0 10px;
               font-size: 12px;
               &:hover { background-color: rgb(248, 250, 252); }
            }
            &.convRow { cursor: pointer; }
         }

         .chevron {
            display: flex;
            align-items: center;
            color: rgb(148, 163, 184);
            margin-right: 6px;
            flex-shrink: 0;
         }

         .convMessagesPanel {
            padding: 0;
            background-color: rgb(248, 250, 252);
            border-bottom: 2px solid rgb(191, 219, 254);
         }

         .convLoading {
            padding: 14px 18px;
            font-size: 12px;
            color: rgb(148, 163, 184);
         }

         .convTotalRow {
            display: flex;
            align-items: center;
            gap: 16px;
            padding: 8px 10px;
            background: linear-gradient(to right, #eff6ff, #f0fdf4);
            border-top: 1px solid rgb(219, 234, 254);
            font-size: 12px;
            font-weight: 600;
            color: rgb(30, 41, 59);

            span:first-child { flex: 1; opacity: 0.6; font-weight: 400; }
         }
      }

      .status {
         display: inline-flex;
         align-items: center;
         gap: 3px;
         padding: 1px 7px;
         height: 18px;
         background-color: rgb(219, 234, 254);
         color: rgb(30, 64, 175);
         border: 1px solid rgb(191, 219, 254);
         border-radius: 20px;
         font-size: 10.5px;
         font-weight: 500;

         &.remove {
            background-color: rgb(254, 226, 226);
            border-color: rgb(254, 202, 202);
            color: rgb(153, 27, 27);
         }
      }

      .keyBadge {
         display: inline-flex;
         align-items: center;
         border-radius: 20px;
         padding: 1px 7px;
         font-size: 10px;
         font-weight: 600;
         text-transform: uppercase;
         letter-spacing: 0.03em;
      }
      .dragifyKey {
         background-color: rgb(219, 234, 254);
         border: 1px solid rgb(191, 219, 254);
         color: rgb(30, 64, 175);
      }
      .userKey {
         background-color: rgb(220, 252, 231);
         border: 1px solid rgb(187, 247, 208);
         color: rgb(22, 101, 52);
      }
      .estimBadge {
         display: inline-flex;
         align-items: center;
         background-color: rgb(254, 243, 199);
         border: 1px solid rgb(253, 230, 138);
         color: rgb(120, 53, 15);
         border-radius: 20px;
         padding: 1px 6px;
         font-size: 10px;
         font-weight: 600;
      }

      .tabBar {
         display: flex;
         gap: 4px;
         margin-bottom: 10px;
         border-bottom: 1px solid rgb(226, 232, 240);
         padding-bottom: 8px;

         button {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            border: 1px solid rgb(226, 232, 240);
            background-color: rgb(248, 250, 252);
            color: rgb(100, 116, 139);
            border-radius: 7px;
            padding: 6px 12px;
            font-size: 12px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.12s;

            &:hover {
               border-color: rgb(191, 219, 254);
               background-color: rgb(239, 246, 255);
               color: rgb(30, 64, 175);
            }
            &.active {
               border-color: rgb(147, 197, 253);
               background-color: rgb(219, 234, 254);
               color: rgb(30, 64, 175);
               font-weight: 600;
            }
         }

         .tabCount {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-width: 18px;
            height: 18px;
            background-color: rgb(30, 64, 175);
            color: #fff;
            border-radius: 20px;
            padding: 0 5px;
            font-size: 10px;
            font-weight: 700;
         }
      }

      .customerSummaryBar {
         display: flex;
         align-items: center;
         flex-wrap: wrap;
         gap: 6px;
         padding: 7px 14px;
         background-color: rgb(239, 246, 255);
         border-bottom: 1px solid rgb(191, 219, 254);
         font-size: 11.5px;
         color: rgb(30, 64, 175);

         > span:first-child {
            display: flex;
            align-items: center;
            gap: 4px;
            font-weight: 600;
            margin-right: 4px;
         }

         .customerChip {
            background-color: rgb(219, 234, 254);
            border: 1px solid rgb(191, 219, 254);
            border-radius: 20px;
            padding: 2px 9px;
            font-size: 11px;
            font-weight: 500;
            cursor: default;
         }
      }
   }

   .empty {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      opacity: 0.3;
      font-size: 13px;
   }
`;